import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropdown", "selectedCurrency"]
  static values = {
    storageKey: { type: String, default: 'selectedCurrency' }
  }

  connect() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside.bind(this))
    
    // Add click handlers to currency options
    this.element.querySelectorAll('.currency-option').forEach(option => {
      option.addEventListener('click', this.handleCurrencySelect.bind(this))
    })

    // Listen for storage events to sync across tabs
    window.addEventListener('storage', this.handleStorageChange.bind(this))

    // Load saved currency
    this.loadSavedCurrency()
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside.bind(this))
    window.removeEventListener('storage', this.handleStorageChange.bind(this))
  }

  toggle() {
    this.dropdownTarget.classList.toggle('hidden')
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.dropdownTarget.classList.add('hidden')
    }
  }

  handleCurrencySelect(event) {
    const currency = event.target.dataset.currency
    const rate = parseFloat(event.target.dataset.rate)
    
    this.updateCurrency(currency, rate)
    
    // Hide dropdown
    this.dropdownTarget.classList.add('hidden')
  }

  handleStorageChange(event) {
    if (event.key === this.storageKeyValue) {
      const { currency, rate } = JSON.parse(event.newValue || '{"currency":"USD","rate":1}')
      this.updateCurrencyDisplay(currency, rate)
    }
  }

  loadSavedCurrency() {
    const saved = localStorage.getItem(this.storageKeyValue)
    if (saved) {
      const { currency, rate } = JSON.parse(saved)
      this.updateCurrencyDisplay(currency, rate)
    } else {
      // Default to USD if no saved currency
      this.updateCurrency('USD', 1)
    }
  }

  updateCurrency(currency, rate) {
    // Save to localStorage
    localStorage.setItem(this.storageKeyValue, JSON.stringify({ currency, rate }))
    
    // Update display
    this.updateCurrencyDisplay(currency, rate)
  }

  updateCurrencyDisplay(currency, rate) {
    // Update selected currency display
    this.selectedCurrencyTarget.textContent = currency
    
    // Update all price elements
    document.querySelectorAll('[data-price]').forEach(element => {
      const basePrice = parseFloat(element.dataset.price)
      const newPrice = this.formatPrice(basePrice * rate, currency)
      element.textContent = newPrice
    })
  }

  formatPrice(amount, currency) {
    // Format based on currency
    switch(currency) {
      case 'USD':
        return `$${this.formatNumber(amount)}`
      case 'EUR':
        return `€${this.formatNumber(amount)}`
      case 'GBP':
        return `£${this.formatNumber(amount)}`
      case 'UGX':
      case 'KES':
      case 'TZS':
      case 'RWF':
        return `${this.formatNumber(amount, 0)} ${currency}`
      default:
        return `${this.formatNumber(amount)} ${currency}`
    }
  }

  formatNumber(number, decimals = 2) {
    const options = {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }
    return number.toLocaleString('en-US', options)
  }
}
